import { CacheService } from '../../../services/cache.service';
import { MapNavigationService } from '../../core/map-navigation.service';
import { MapFeatureColors } from '../../core/map-object-colors';
import { DensityCellData, DensitySource, densityParams } from '../../core/density';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, map, tap } from 'rxjs';
import L from 'leaflet';

import {
  PlaceFeatureBase,
  PlaceMapObject,
  MapSelectionObject,
} from '../../core/base/place-feature.base';
import { MapObjectPopoverSection } from '../../core/map-object-popover';
import { MapObjectDetailsService } from '../../../services/map-object-details.service';
import { PlaceMarkerLayoutService } from '../../core/place-marker-layout.service';
import {
  MapTileCache,
  MapTileResponse,
  MapTotalResponse,
  MapViewport,
  MAP_CLUSTER_MODE_SWITCH_ZOOM,
} from '../../core/map-tile-cache';
import { mapSelection } from '../../core/spatial/map-viewport';
import type { MapTile } from '../../core/spatial/map-viewport';

export interface LeadMapObject extends PlaceMapObject {
  readonly id: number;
  readonly boardColumn: string;
  readonly boardColumnCode: string;
  readonly title: string | null;
  readonly contributionBase: number | null;
}

export interface LeadMapFilter {
  readonly column: readonly string[] | null;
  readonly salesRep: number | null;
  readonly search: string;
  readonly territoryId: number | null;
  readonly territoryOwner: number | null;
  readonly highContributionOnly: boolean;
}

export const EMPTY_LEAD_MAP_FILTER: LeadMapFilter = {
  column: null,
  salesRep: null,
  search: '',
  territoryId: null,
  territoryOwner: null,
  highContributionOnly: false,
};

@Injectable({ providedIn: 'root' })
export class LeadFeature extends PlaceFeatureBase<LeadMapObject> implements DensitySource {
  override readonly page = '/leads';
  readonly densityChanged$ = new Subject<void>();

  loadDensity(grid: MapViewport): Observable<readonly DensityCellData[]> {
    return this.http.get<readonly DensityCellData[]>(
      '/api/leads/map/density',
      { params: densityParams(leadFilterParams(this.activeFilter), grid) },
    );
  }

  static readonly Key = 'leads';
  static readonly MinAnnualContribution = 10000;

  private readonly tileCache = new MapTileCache<LeadMapObject>(
    this.cache, 'map:leads', point => point.id,
  );
  private viewport?: MapViewport;
  private activeFilter: LeadMapFilter = EMPTY_LEAD_MAP_FILTER;
  get contextState(): Readonly<Record<string, unknown>> { return { filters: { ...this.activeFilter } }; }
  private request?: Subscription;
  private totalRequest?: Subscription;
  private initialized = false;
  private refreshWhenEnabled = false;

  constructor(
    private readonly http: HttpClient,
    details: MapObjectDetailsService,
    markerLayout: PlaceMarkerLayoutService,
    cache: CacheService,
    navigation: MapNavigationService,
  ) {
    super({
      key: LeadFeature.Key,
      name: 'Leads',
      color: MapFeatureColors.leads,
    }, details, markerLayout, cache, navigation);
  }

  override resetSession(): void {
    this.request?.unsubscribe();
    this.totalRequest?.unsubscribe();
    this.viewport = undefined;
    this.activeFilter = EMPTY_LEAD_MAP_FILTER;
    this.initialized = false;
    this.refreshWhenEnabled = false;
    super.resetSession();
  }

  async init(): Promise<void> {
    this.initialized = true;
    this.refreshTotal();
    this.refresh();
  }

  filter(filter: LeadMapFilter): void {
    this.activeFilter = { ...filter };
    this.densityChanged$.next();
    if (!this.initialized) return;
    this.refreshTotal();
    this.refresh();
  }

  override onViewportChanged(viewport: MapViewport): void {
    this.viewport = viewport;
    if (this.initialized) this.refresh();
  }

  refresh(): void {
    if (!this.viewport) return;
    if (!this.enabled) {
      this.refreshWhenEnabled = true;
      return;
    }
    this.refreshWhenEnabled = false;
    this.request?.unsubscribe();
    this.setLoading(true);
    const viewport = this.viewport;
    const filter = this.activeFilter;
    const clusterMode = viewport.zoom < MAP_CLUSTER_MODE_SWITCH_ZOOM;
    const request = this.tileCache.filter(
      viewport,
      leadFilterKey(filter),
      requestViewport => this.load(filter, requestViewport),
    );
    this.request = request.subscribe({
      next: response => {
        if (clusterMode) {
          this.replaceClusteredObjects(
            response.points,
            response.clusters ?? [],
          );
        } else {
          this.replaceObjects(response.points);
        }
      },
      error: () => this.setLoading(false),
      complete: () => this.setLoading(false),
    });
  }

  /** Resolve the current canonical Place, even when this Lead is outside loaded map tiles. */
  focusById(id: number): Observable<void> {
    return this.http.get<LeadMapObject>(`/api/leads/map/${id}`).pipe(
      tap(object => this.focusObject(object)), map(() => undefined),
    );
  }

  clearCacheAndRefresh(): void {
    this.densityChanged$.next();
    this.tileCache.clear();
    this.refreshTotal();
    this.refresh();
  }

  override render(): void {
    if (this.enabled && this.refreshWhenEnabled) {
      this.refresh();
      return;
    }
    super.render();
  }

  protected objectPopover(object: LeadMapObject): MapObjectPopoverSection {
    return {
      type: 'Lead',
      title: object.title?.trim() || `Lead ${object.id}`,
      values: [
        { label: 'Stage', value: object.boardColumn },
        { label: 'Annual contribution', value: object.contributionBase },
      ],
    };
  }

  protected objectId(object: LeadMapObject): number {
    return object.id;
  }

  protected selectObjects(
    bounds: L.LatLngBounds,
    polygon?: readonly L.LatLng[],
    clusterTiles?: readonly MapTile[],
  ): Observable<readonly MapSelectionObject[]> {
    return this.http.post<{ objects: readonly MapSelectionObject[] }>(
      '/api/leads/map/selection',
      mapSelection(bounds, polygon, clusterTiles),
      { params: leadFilterParams(this.activeFilter) },
    ).pipe(map(response => response.objects));
  }

  private load(
    filter: LeadMapFilter,
    viewport: MapViewport,
  ): Observable<MapTileResponse<LeadMapObject>> {
    const params = leadFilterParams(filter)
      .set('south', viewport.south)
      .set('west', viewport.west)
      .set('north', viewport.north)
      .set('east', viewport.east)
      .set('zoom', Math.floor(viewport.zoom));

    return this.http.get<MapTileResponse<LeadMapObject>>('/api/leads/map', {
      params,
    });
  }

  private refreshTotal(): void {
    this.totalRequest?.unsubscribe();
    this.totalRequest = this.http.get<MapTotalResponse>('/api/leads/total', {
      params: leadFilterParams(this.activeFilter),
    }).subscribe({
      next: response => this.setTotal(response.total),
      error: () => undefined,
    });
  }
}

function leadFilterParams(filter: LeadMapFilter): HttpParams {
  let params = new HttpParams();
  for (const column of filter.column ?? []) params = params.append('column', column);
  if (filter.salesRep != null) params = params.set('salesRep', filter.salesRep);
  if (filter.search.trim()) params = params.set('search', filter.search.trim());
  if (filter.territoryId != null) {
    params = params.set('territoryId', filter.territoryId);
  }
  if (filter.territoryOwner != null) {
    params = params.set('territoryOwner', filter.territoryOwner);
  }
  if (filter.highContributionOnly) {
    params = params.set('minContribution', LeadFeature.MinAnnualContribution);
  }

  return params;
}

function leadFilterKey(filter: LeadMapFilter): string {
  return JSON.stringify({
    column: filter.column,
    salesRep: filter.salesRep,
    search: filter.search.trim(),
    territoryId: filter.territoryId,
    territoryOwner: filter.territoryOwner,
    highContributionOnly: filter.highContributionOnly,
  });
}
